import React, { Component } from 'react';
import { Col, Container, Row, Form } from 'react-bootstrap';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import axios from "axios";
import Select from '../../../Components/PCSelect';
import WeekRangeSelect from '../../../Components/WeekRangeSelect';
import Swal from "sweetalert2";
import { formatDate, formatDisplayWeek, sumMoney, getWeekDetails, getWeekDayList, money, approvalActions, hasAction, isSuperAdmin, formatAmount } from '../../../helper';
import PhoneClinicGrid from '../../../Components/PhoneClinicGrid';
import RowActions from '../../../Components/PhoneClinicGrid/RowActions';
import { CARD as PC_CARD, COL_HEAD, LABEL, AMOUNT_INPUT, DATE_INPUT, DESC_INPUT, DESC_CLASS, WRAP_SELECT, fitDescription, keepOneLine } from '../shared/txStyles';
import StatusChip from '../shared/StatusChip';
import BulkActionBar from '../../../Components/PhoneClinicGrid/BulkActionBar';
import { runTransition, APPROVAL_MODULES } from '../shared/approvalApi';

import AmountInput from '../../../Components/AmountInput';
import PageHeader from '../shared/PageHeader';
import withRouter from '../../../helper/withRouter';
import { recordOpened, recordClosed, recordParamChanged, recordNotFound } from '../../../helper/recordLinks';
import { ownLimit, limitOptions } from '../../../helper/identity';

const BASE_PATH = '/admin/account-payments';   // + '/<PUBLIC_ID>' opens one record
/* Unified card = shared token + this page's layout extras. */
const CARD = { ...PC_CARD, overflow: 'hidden', marginBottom: 24 };

class AccountPayments extends Component {
    constructor(props) {
        super(props);
        this.state = {
            addAccPayment: false,
            isEdit: false,
            AccPaymentsList: [],
            ShopList: [],
            AccountList: [],
            AllWareHouseList: [],
            groupedOptions: [],
            AP_ID: "",
            PaymentWeek: "",
            ShopPayment: "",
            PrevWeek: "0",
            CWCard: "0",
            TotalAmount: "0",
            NetAmount: "0",
            Expense: "0",
            AccountPayableDetails: [],
            shopPaymentDays: [],
            PC_USR_ROLE_ID: localStorage.getItem('PC_USR_ROLE_ID'),
            PC_GRP_ID: localStorage.getItem('PC_GRP_ID'),
            PC_SHOP_ID: localStorage.getItem('PC_GRP_ATT'),
            isAccountDisable: false,
            AP_STATUS: "PENDING",
            // Only whether to RENDER the bulk actions. Every transition is re-checked
            // server-side against pc_role_rights from the signed token.
            canApprove: hasAction('/admin/account-payments', 'APPROVE'),
            selectedIds: [],
            bulkBusy: false,
        };
        this.gridRef = React.createRef(null);
        this.openPid = null;   // public id of the record the form shows (helper/recordLinks.js)
    }

    async componentDidMount() {
        const [ShopList, AccountList, AllWareHouseList] = await Promise.all([
            this.getShopList(),
            this.getAllPayaccount(),
            this.getWarehouseList(),
        ]);
        this.setState({
            // One "Charge To" selector per line, offering Shop / Account / Warehouse.
            // Replaced the old two-selector layout on 2026-10-13.
            groupedOptions: this.buildGroupedOptions(ShopList, AccountList, AllWareHouseList),
        }, () => {
            // A record link (/admin/account-payments/<PUBLIC_ID>) opens once the account list the
            // edit form needs is in state — this callback runs after that update is applied.
            const pid = this.props.params?.publicId;
            if (pid) this.openByPublicId(pid);
        });
    }

    componentDidUpdate(prevProps) {
        recordParamChanged(this, prevProps, (pid) => this.openByPublicId(pid), () => this.handleExit());
    }

    // The record behind a link, from the same list endpoint with the server applying the signed-in
    // user's scope — then the normal edit form, exactly as the list's Edit button opens it.
    async openByPublicId(pid) {
        this.openPid = pid;
        this.props.toggleLoader(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/Finance/PCGetAllAccountPayment.php`, {
                PC_USR_ROLE_ID: this.state.PC_USR_ROLE_ID, PC_GRP_ID: this.state.PC_GRP_ID,
                PC_SHOP_ID: this.state.PC_SHOP_ID, PUBLIC_ID: pid,
            });
            const row = res.data?.success === 1 ? res.data.data[0] : null;
            this.props.toggleLoader(false);
            if (!row) return recordNotFound(this, BASE_PATH);
            this.handleUpdateAccPay(row);
        } catch (error) {
            this.props.toggleLoader(false);
            console.error('openByPublicId error:', error);
            recordNotFound(this, BASE_PATH);
        }
    }

    // The list's Edit button: same form as before, and the record's link in the address bar.
    openFromList(item) {
        recordOpened(this, BASE_PATH, item.PUBLIC_ID);
        this.handleUpdateAccPay(item);
    }

    async getAccountPayments(searchData = [], pagination = { recordOffset: 1, pageSize: 50 }) {
        this.props.toggleLoader(true);
        const formData = {
            PC_USR_ROLE_ID: this.state.PC_USR_ROLE_ID,
            PC_GRP_ID: this.state.PC_GRP_ID,
            PC_SHOP_ID: this.state.PC_SHOP_ID,
            searchData,
            pageSize: pagination.pageSize,
            recordOffset: pagination.recordOffset
        };
        const getSP = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/Finance/PCGetAllAccountPayment.php`, formData);
        const ShopPaymentData = getSP.data;
        let AccPaymentsList = [];
        if (ShopPaymentData.success === 1) {
            AccPaymentsList = ShopPaymentData.data;
            pagination.totalItems = parseInt(ShopPaymentData.Page_count[0]['TOTAL_PAGE_COUNT']);
            pagination.totalPages = Math.ceil(pagination.totalItems / pagination.pageSize || 50);
            this.gridRef.current?.setPaginationData(pagination);
        }
        this.setState({ AccPaymentsList });
        this.props.toggleLoader(false);
    }

    async getShopList() {
        const getShops = await axios.get(`${process.env.REACT_APP_BASE_URL}services/query/PCGetShops.php`);
        const shopData = getShops.data;
        let ShopList = [];
        if (shopData.success === 1) {
            ShopList = shopData.data.map(shop => ({ value: shop.PC_SP_ID, label: shop.SP_NAME, type: "shop" }));
        }
        this.setState({ ShopList });
        return ShopList;
    }

    async getWarehouseList() {
        const getWarehouse = await axios.get(`${process.env.REACT_APP_BASE_URL}services/query/PCGetAllWarehouse.php`);
        const warehouseData = getWarehouse.data;
        let AllWareHouseList = [];
        if (warehouseData.success === 1) {
            AllWareHouseList = warehouseData.data.map(warehouse => ({
                value: warehouse.WAREHOUSE_ID,
                label: warehouse.WAREHOUSE_NAME,
                type: "warehouse"
            }));
        }
        this.setState({ AllWareHouseList });
        return AllWareHouseList;
    }

    buildGroupedOptions(ShopList, AccountList, AllWareHouseList) {
        return [
            { label: "Shop", options: ShopList },
            { label: "Account", options: AccountList },
            { label: "Warehouse", options: AllWareHouseList }
        ];
    }

    async getAllPayaccount() {
        const getAllPayAccounts = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/PCGetAllPayAccount.php`);
        const deviceData = getAllPayAccounts.data;
        let AccountList = [];
        if (deviceData.success === 1) {
            AccountList = deviceData.data.map(eachAccount => ({
                label: eachAccount.ACCOUNT_TITLE,
                value: eachAccount.ACCOUNT_ID,
                type: "account"
            }));
        }
        this.setState({ AccountList, AllAccountData: deviceData.data });
        return AccountList;
    }

    // The Account box's options: the user's own accounts (every account for an admin). AccountList
    // itself stays every account — it also feeds Charge To.
    ownAccountOptions() {
        return limitOptions(this.state.AccountList, ownLimit('account', BASE_PATH), { type: "account" });
    }

    async hanldeAddAccPayment() {
        let isAccountDisable = false;
        let ShopPayment = "";
        let AccountPayableDetails = [];

        // The Account box lists only the user's own account (selected and locked); admins pick
        // from every account (user, 2026-11-02). Charge To keeps every shop / account / warehouse.
        const ownAccounts = this.ownAccountOptions();
        if (ownLimit('account', BASE_PATH) && ownAccounts.length === 1) {
            isAccountDisable = true;
            ShopPayment = ownAccounts[0];
            AccountPayableDetails = Array.from({ length: 10 }, () => ({
                ShopPay_Date: "",
                ShopPay_Acc: "",
                ShopPay_Desc: "",
                ShopPay_Amnt: 0
            }));
        }

        // Preselect the current week and seed its day rows, the same way picking a week by
        // hand does — the day rows bound the per-line date pickers.
        const PaymentWeek = getWeekDetails().weekId;
        const WeekOut = await getWeekDayList(PaymentWeek);
        const shopPaymentDays = WeekOut.map(WeekPerDay => ({ DateAndDay: WeekPerDay }));

        this.setState({ addAccPayment: true, isEdit: false, isAccountDisable, ShopPayment, AccountPayableDetails, PaymentWeek, shopPaymentDays });
    }

    async handleChange(fieldName, evt) {
        let value;
        switch (fieldName) {
            case 'ShopPayment': {
                value = evt;
                const AccountPayableDetails = Array.from({ length: 10 }, () => ({
                    ShopPay_Date: "",
                    ShopPay_Acc: "",
                    ShopPay_Desc: "",
                    ShopPay_Amnt: 0
                }));
                this.setState({ AccountPayableDetails });
                break;
            }
            case 'PaymentWeek': {
                value = evt.target.value;
                const WeekOut = await getWeekDayList(value);
                const shopPaymentDays = WeekOut.map(WeekPerDay => ({ DateAndDay: WeekPerDay }));
                this.setState({ shopPaymentDays });
                break;
            }
            default:
                value = evt.target.value;
                break;
        }
        this.setState({ [fieldName]: value });
    }

    handleChangeShopPayment(index, fieldName, evt) {
        const AccountPayableDetails = [...this.state.AccountPayableDetails];
        let targetValue;
        switch (fieldName) {
            case 'ShopPay_Acc':
                targetValue = evt ? evt : "";
                break;
            default:
                targetValue = evt.target.value;
                break;
        }
        AccountPayableDetails[index] = { ...AccountPayableDetails[index], [fieldName]: targetValue };
        this.setState({ AccountPayableDetails }, () => {
            if (fieldName === 'ShopPay_Amnt') {
                const { AccountPayableDetails, TotalAmount } = this.state;
                const ExtraSum = sumMoney(AccountPayableDetails, "ShopPay_Amnt");
                this.setState({ Expense: ExtraSum, NetAmount: +TotalAmount - ExtraSum });
            }
        });
    }

    handleValidate() {
        if (!this.state.PaymentWeek) {
            Swal.fire('Error', 'Please Select Payment Week', 'error');
            return false;
        }
        if (!this.state.ShopPayment) {
            Swal.fire('Error', 'Please Select Account', 'error');
            return false;
        }
        return true;
    }

    async handleSubmitAccPayment() {
        if (!this.handleValidate()) return;
        const { AccountPayableDetails } = this.state;

        /* One charge target per line (ShopPay_Acc). The old two-bucket split lived here —
         * "rows with a TO but no FROM shop" vs "rows with BOTH" — and fed ShopDataSum /
         * WeekSaleDetail. Both ended up in pc_shop_master.PCM_PAID_FROM_WEEK / PCM_PAID_FROM_ACC,
         * which no endpoint, report or legacy procedure reads, and pc_shop_master holds 0 rows,
         * so the UPDATE matched nothing. The two keys are still sent so PCAddAccountPayment.php
         * keeps its payload contract; WeekSaleDetail now simply carries every charged line.
         */

        /* Drop the pre-2026-10-13 "on behalf of" tag on save. It has to go: warehouseBalance.php
         * charges a warehouse off this field, so leaving it would keep charging the warehouse AND
         * the shop from one line — the double charge the single field exists to end. */
        const linesToStore = AccountPayableDetails.map(({ ShopPay_Shop, ...line }) => line);
        const chargedLines = linesToStore.filter(x => x.ShopPay_Acc !== "" && x.ShopPay_Acc);

        const formData = {
            AP_ID: this.state.AP_ID,
            PaymentWeek: this.state.PaymentWeek,
            ShopPayment: this.state.ShopPayment?.value,
            PrevWeek: this.state.PrevWeek.toString(),
            CWCard: this.state.CWCard.toString(),
            TotalAmount: this.state.TotalAmount.toString(),
            NetAmount: this.state.NetAmount.toString(),
            Expense: this.state.Expense.toString(),
            AccountPayableDetails: JSON.stringify(linesToStore),
            shopPaymentDays: JSON.stringify(this.state.shopPaymentDays),
            WeekSaleDetail: JSON.stringify(chargedLines),
            AP_STATUS: "PENDING",
            LastUpdateBY: localStorage.getItem('PC_USR_NAME'),
            ShopDataSum: 0
        };

        try {
            const getSaleResult = await axios.post(`${process.env.REACT_APP_BASE_URL}services/mutation/Finance/PCAddAccountPayment.php`, formData);
            const linkData = {
                ShopPayment: this.state.ShopPayment?.value,
                AccountPayableDetails: JSON.stringify(linesToStore),
                PaymentWeek: this.state.PaymentWeek
            };
            await axios.post(`${process.env.REACT_APP_BASE_URL}services/linking/AccountPaymentUpdate.php`, linkData);

            if (getSaleResult.data.status === 201) {
                Swal.fire('Success', getSaleResult.data.message, 'success').then(res => {
                    if (getSaleResult.data.message === 'Account Payment Opened') {
                        if (res.isConfirmed) {
                            this.setState({ isEdit: true, AP_ID: getSaleResult.data.extra[0].AP_ID });
                            // A new payment now has a link of its own.
                            recordOpened(this, BASE_PATH, getSaleResult.data.extra[0].PUBLIC_ID, { replace: true });
                        }
                    } else if (res.isConfirmed) {
                        // Updated: stay on the record, as Weekly Sale and Shop Payments do (T1.76).
                        this.reloadOpenRecord();
                    } else {
                        this.handleExit();
                    }
                });
            } else {
                Swal.fire('Error', getSaleResult.data.message, 'error');
            }
        } catch (error) {
            console.error('handleSubmitAccPayment error:', error);
            Swal.fire('Error', 'Failed to save payment', 'error');
        }
    }

    handleExit() {
        recordClosed(this, BASE_PATH);
        this.getAccountPayments();
        this.setState({
            isEdit: false,
            addAccPayment: false,
            AP_ID: "",
            PaymentWeek: "",
            ShopPayment: "",
            PrevWeek: "0",
            CWCard: "0",
            TotalAmount: "0",
            NetAmount: "0",
            Expense: "0",
            AccountPayableDetails: [],
            shopPaymentDays: [],
            AP_STATUS: "PENDING",
        });
    }

    handleUpdateAccPay(item) {
        const { AccountList } = this.state;
        const WeeklyShop = AccountList.find(x => +x.value === +item.AP_SHOP_ID) || "";
        this.setState({
            isEdit: true,
            addAccPayment: true,
            isAccountDisable: true,
            AP_ID: item.AP_ID,
            PaymentWeek: item.AP_WEEK,
            ShopPayment: WeeklyShop,
            PrevWeek: item.AP_PRE_WEEK,
            CWCard: item.AP_CWCard,
            TotalAmount: item.AP_TOTAMT,
            NetAmount: item.AP_NTAMT,
            Expense: item.AP_EXP,
            AccountPayableDetails: JSON.parse(item.AP_DETAILS),
            shopPaymentDays: JSON.parse(item.AP_WEEK_DAYS),
            AP_STATUS: item.AP_STATUS
        });
    }

    // Shop users (PC_GRP_ID 2) send a pending payment for approval (PENDING → REQUESTED);
    // admins approve (→ APPROVED). Mirrors WeeklySale.handleSendForApproval. Backend
    // (PCRequestAccountPayment.php) enforces the role/transition rules.

    /* Bulk transition for the rows ticked in the list. Routed through the same shared
     * endpoint the per-record action uses (services/mutation/Approval/PCApproveFinance.php),
     * so the rules cannot drift between one record and fifty. Partial results are normal
     * and are reported per record by reportTransition(). */
    async handleBulkTransition(to, ids) {
        const confirm = await Swal.fire({
            title: `Update ${ids.length} account payment${ids.length === 1 ? '' : 's'}?`,
            text: `They will be marked ${to.toLowerCase()}.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        });
        if (!confirm.isConfirmed) return;

        this.setState({ bulkBusy: true });
        const result = await runTransition(APPROVAL_MODULES.ACCOUNT_PAYMENT, to, ids);
        this.setState({ bulkBusy: false, selectedIds: [] });
        if (result && result.approved > 0) this.getAccountPayments();
    }

    async handleSendForApproval(Status) {
        /* Routed through the SAME shared endpoint the bulk bar uses, with a list of one.
         *
         * This replaced services/mutation/Finance/PCRequestAccountPayment.php, which took the caller's identity
         * from the request body (PC_GRP_ID / PC_SHOP_ID) and gated on a hardcoded
         * "PC_GRP_ID == '2'". PCApproveFinance.php reads the role AND the owning shop from the
         * SIGNED token instead, so a caller can no longer claim to be an admin. */
        this.props.toggleLoader(true);
        /* The loader is hidden by the callback, BEFORE the dialog opens — it is a z-index
         * 10000 overlay and would otherwise sit on top of the dialog, leaving it visible but
         * unclickable and the page permanently blocked. */
        const result = await runTransition(APPROVAL_MODULES.ACCOUNT_PAYMENT, Status, [this.state.AP_ID],
            () => this.props.toggleLoader(false));
        if (result && result.approved > 0) await this.reloadOpenRecord();
    }

    /* After an approval action, stay on the record and show its new status (user, 2026-11-05,
     * T1.76): reload it from the server. Falls back to the list when no record link is open. */
    async reloadOpenRecord() {
        if (!this.openPid) return this.handleExit();
        await this.openByPublicId(this.openPid);
        this.getAccountPayments(); // the list behind the record shows the new status too
    }

    async handleDelete(AP_ID) {
        Swal.fire({
            title: 'Do you want to delete Account Payment?',
            icon: "question",
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then(async (result) => {
            if (result.isConfirmed) {
                this.props.toggleLoader(true);
                const res = await axios.post(`${process.env.REACT_APP_BASE_URL}services/delete/finance/DeleteAccountPayment.php`, { AP_ID });
                if (res.data.success === 1) {
                    Swal.fire('Account Payment Deleted!', '', 'success');
                    this.getAccountPayments();
                }
                this.props.toggleLoader(false);
            }
        });
    }

    handleAddTableItem() {
        const AccountPayableDetails = [...this.state.AccountPayableDetails, {
            ShopPay_Date: "",
            ShopPay_Acc: "",
            ShopPay_Desc: "",
            ShopPay_Amnt: 0
        }];
        this.setState({ AccountPayableDetails });
    }

    handleRemoveTableItem(index) {
        const AccountPayableDetails = this.state.AccountPayableDetails.filter((_, i) => i !== index);
        this.setState({ AccountPayableDetails });
    }

    render() {
        const { addAccPayment, shopPaymentDays, AccountPayableDetails } = this.state;
        const isFieldDisable = this.state.isEdit && this.state.addAccPayment;
        const totalExpense = money(this.state.Expense);

        // Shop users (PC_GRP_ID 2) may only act while the payment is PENDING; once it is
        // REQUESTED/APPROVED they can no longer edit or delete it. Admins keep the old rule
        // (editable until APPROVED). Mirrors WeeklySale.
        const isShopUser = this.state.PC_GRP_ID === '2';
        const status = this.state.AP_STATUS;
        // Reopen is offered only to SUPER_ADMIN, and only on a record that is not already pending.
        const canReopen = isSuperAdmin() && status !== "PENDING";
        // Super Admin may save changes at ANY status and the record keeps it (T1.75, user; the
        // server applies the same rule via helper/Approval.php pc_approval_lock_check).
        const canEdit = isShopUser ? status === 'PENDING' : (status !== 'APPROVED' || isSuperAdmin());

        if (!addAccPayment) {
            return (
                <Container>
                    <BulkActionBar
                        rows={this.state.AccPaymentsList.filter(r => this.state.selectedIds.includes(String(r.AP_ID)))}
                        statusField="AP_STATUS"
                        idField="AP_ID"
                        canApprove={this.state.canApprove}
                        busy={this.state.bulkBusy}
                        onAction={this.handleBulkTransition.bind(this)}
                        onClear={() => this.setState({ selectedIds: [] })}
                    />
                    <PhoneClinicGrid
                        selection={{
                            rowIds: this.state.AccPaymentsList.map(r => String(r.AP_ID)),
                            // Only rows with a transition available to this user can be ticked.
                            selectableIds: this.state.AccPaymentsList
                                .filter(r => approvalActions(r.AP_STATUS, this.state.canApprove).length > 0)
                                .map(r => String(r.AP_ID)),
                            selected: this.state.selectedIds,
                            onChange: (ids) => this.setState({ selectedIds: ids }),
                        }}
                        ref={this.gridRef}
                        title="ACCOUNT PAYMENT"
                        buttonLabel="Account Payment"
                        isEditable={true}
                        onClickButton={this.hanldeAddAccPayment.bind(this)}
                        getData={this.getAccountPayments.bind(this)}
                        tableHeadData={[
                            { label: "WEEK", isHidden: false },
                            { label: "ACCOUNT", isHidden: false },
                            { label: "WEEKLY EXPENSE", isHidden: false },
                            { label: "CREATED BY", isHidden: false },
                            { label: "STATUS", isHidden: false },
                        ]}
                    >
                        {this.state.AccPaymentsList.map((service, idx) => {
                            const FilterShop = this.state.AccountList.find(x => +x.value === +service.AP_SHOP_ID);
                            return (
                                <TableRow
                                    key={idx}
                                    /* The grid reads this to know which record a checkbox selects. */
                                    data-pcid={String(service.AP_ID)}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    style={{ whiteSpace: 'nowrap' }}
                                >
                                    <TableCell align='left'>{formatDisplayWeek(service.AP_WEEK)}</TableCell>
                                    <TableCell align='left' style={{ cursor: 'pointer', color: "#416bc7" }}>{FilterShop?.label ?? service.AP_SHOP_ID}</TableCell>
                                    <TableCell align='left'>{formatAmount(service.AP_EXP)}</TableCell>
                                    <TableCell align='left'>{service.CREATED_BY}</TableCell>
                                    <TableCell align='left'>
                                        <StatusChip label={service.AP_STATUS} />
                                    </TableCell>
                                    <TableCell align='center'>
                                        <RowActions actions={[
                                            { type: 'edit', onClick: this.openFromList.bind(this, service) },
                                            { type: 'delete', onClick: this.handleDelete.bind(this, service.AP_ID), show: (!isShopUser || service.AP_STATUS === "PENDING") },
                                        ]} />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </PhoneClinicGrid>
                </Container>
            );
        }

        return (
            <Container fluid style={{ padding: '8px 16px' }}>
                <PageHeader title={this.state.isEdit ? 'Edit Account Payment' : 'Add Account Payment'}
                    mode={this.state.isEdit ? 'edit' : 'new'} />
                {/* ── Filter bar ───────────────────────────────────────── */}
                <div style={CARD}>
                    <div style={{ padding: '16px 24px' }}>
                        <Row className="align-items-end g-3">
                            <Col md={4}>
                                <label style={LABEL}>
                                    Week
                                    {shopPaymentDays.length > 0 && (
                                        <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#94a3b8', marginLeft: 6 }}>
                                            ({shopPaymentDays[0].DateAndDay} – {shopPaymentDays[6].DateAndDay})
                                        </span>
                                    )}
                                </label>
                                <WeekRangeSelect
                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    disabled={isFieldDisable}
                                    value={this.state.PaymentWeek}
                                    onChange={this.handleChange.bind(this, "PaymentWeek")}
                                />
                            </Col>
                            <Col md={4}>
                                <label style={LABEL}>Account</label>
                                <Select menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    isDisabled={this.state.isAccountDisable}
                                    value={this.state.ShopPayment}
                                    options={this.ownAccountOptions()}
                                    onChange={this.handleChange.bind(this, "ShopPayment")}
                                />
                            </Col>
                            <Col md={4}>
                                <label style={LABEL}>Total Expense (GBP)</label>
                                <div style={{ position: 'relative' }}>
                                    <AmountInput
                                        disabled
                                        value={totalExpense}
                                        style={{ padding: '7px 36px 7px 10px', borderRadius: 8, fontSize: 14, fontWeight: 700, background: '#f1f3ff', color: '#a32f2f', border: '1px solid #e5e7eb', textAlign: 'right' }}
                                    />
                                    <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontWeight: 700, color: '#a32f2f', pointerEvents: 'none' }}>£</span>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* ── Payment entries table ────────────────────────────── */}
                {this.state.isEdit && (
                    <div style={CARD}>
                        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa' }}>
                            <span style={{ fontWeight: 700, fontSize: 17, color: '#0b1c30' }}>Payment Entries</span>
                            <button
                                onClick={this.handleAddTableItem.bind(this)}
                                style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#a32f2f', color: 'white', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                            >
                                <AddIcon style={{ fontSize: 18 }} /> Add Entry
                            </button>
                        </div>
                        <TableContainer sx={{ maxHeight: '60vh' }}>
                            <Table size="small" stickyHeader className="pc-card-table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ ...COL_HEAD, width: 48 }}>SNo</TableCell>
                                        <TableCell style={{ ...COL_HEAD, width: 160 }}>Date</TableCell>
                                        <TableCell style={{ ...COL_HEAD, width: 260 }}>Charge To</TableCell>
                                        <TableCell style={COL_HEAD}>Description</TableCell>
                                        <TableCell style={{ ...COL_HEAD, textAlign: 'right', width: 130 }}>Amount</TableCell>
                                        <TableCell style={{ ...COL_HEAD, textAlign: 'center', width: 56 }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {AccountPayableDetails.map((service, idx) => (
                                        <TableRow
                                            key={idx}
                                            sx={{ '&:hover': { background: '#f8faff' }, '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell style={{ fontSize: 13, color: '#64748b', padding: '6px 16px' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ padding: '4px 12px' }}>
                                                <Form.Control
                                                    name="ShopPay_Date"
                                                    type="date"
                                                    style={DATE_INPUT}
                                                    min={shopPaymentDays.length > 0 ? formatDate(shopPaymentDays[0].DateAndDay) : ''}
                                                    max={shopPaymentDays.length > 0 ? formatDate(shopPaymentDays[6].DateAndDay) : ''}
                                                    value={service.ShopPay_Date}
                                                    onChange={this.handleChangeShopPayment.bind(this, idx, 'ShopPay_Date')}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ padding: '4px 12px' }}>
                                                <Select menuPortalTarget={document.body}
                                                    name="ShopPay_Acc"
                                                    menuPlacement="auto"
                                                    isClearable
                                                    value={service.ShopPay_Acc}
                                                    options={this.state.groupedOptions}
                                                    onChange={this.handleChangeShopPayment.bind(this, idx, 'ShopPay_Acc')}
                                                    styles={{ ...WRAP_SELECT, control: base => ({ ...base, minHeight: 34, fontSize: 14 }) }}
                                                />
                                                {/* A line saved before 2026-10-13 may still carry the old
                                                    "on behalf of" account/warehouse. It is shown read-only so it
                                                    is not invisible, and flagged because saving this record
                                                    rewrites the line as a charge to the field above only. */}
                                                {service.ShopPay_Shop && service.ShopPay_Shop.label && (
                                                    <div
                                                        title={'Previously also recorded against ' + service.ShopPay_Shop.label + '. Saving this record will charge only the selection above.'}
                                                        style={{ fontSize: 11, color: '#92400e', marginTop: 3 }}
                                                    >
                                                        was: {service.ShopPay_Shop.label}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ padding: '4px 12px' }}>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={1}
                                                    name="ShopPay_Desc"
                                                    className={DESC_CLASS}
                                                    style={DESC_INPUT}
                                                    ref={(el) => fitDescription(el)}
                                                    title={service.ShopPay_Desc || undefined}
                                                    onKeyDown={keepOneLine}
                                                    value={service.ShopPay_Desc}
                                                    onChange={this.handleChangeShopPayment.bind(this, idx, 'ShopPay_Desc')}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ padding: '4px 12px' }}>
                                                <AmountInput
                                                    name="ShopPay_Amnt"
                                                    style={{ ...AMOUNT_INPUT, textAlign: 'right', fontWeight: 600 }}
                                                    value={service.ShopPay_Amnt}
                                                    onChange={this.handleChangeShopPayment.bind(this, idx, 'ShopPay_Amnt')}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ padding: '4px 8px', textAlign: 'center' }}>
                                                <button
                                                    onClick={this.handleRemoveTableItem.bind(this, idx)}
                                                    style={{ background: 'none', border: 'none', color: '#ba1a1a', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'flex', alignItems: 'center' }}
                                                >
                                                    <RemoveCircleOutlineIcon style={{ fontSize: 20 }} />
                                                </button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Table footer actions */}
                        <div style={{ padding: '14px 20px', borderTop: '1px solid #f1f5f9', background: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', gap: 16 }}>
                                <button
                                    onClick={this.handleExit.bind(this)}
                                    style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                                >
                                    Cancel Changes
                                </button>
                                <button
                                    style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: 0 }}
                                >
                                    <HistoryIcon style={{ fontSize: 18 }} /> View Audit Log
                                </button>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' }}>SUBTOTAL</div>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: '#a32f2f' }}>£ {formatAmount(totalExpense)}</div>
                                </div>
                                {canEdit && (
                                    <Button
                                        variant="contained"
                                        onClick={this.handleSubmitAccPayment.bind(this)}
                                        style={{ background: '#a32f2f', textTransform: 'none', fontWeight: 600, borderRadius: 8, padding: '10px 32px', boxShadow: '0 4px 12px rgba(163,47,47,0.25)' }}
                                    >
                                        Confirm Payment Batch
                                    </Button>
                                )}
                                {/* Shop users can only send for approval (PENDING → REQUESTED); admins can also approve. */}
                                {(isShopUser ? status === "PENDING" : status !== "APPROVED") && (
                                    <Button
                                        variant="contained"
                                        onClick={this.handleSendForApproval.bind(this, status === "PENDING" ? "REQUESTED" : "APPROVED")}
                                        style={{ background: '#0b1c30', textTransform: 'none', fontWeight: 600, borderRadius: 8, padding: '10px 32px', boxShadow: '0 4px 12px rgba(11,28,48,0.25)' }}
                                    >
                                        {status === "PENDING" ? "Send For Approval" : "Approve"}
                                    </Button>
                                )}
                                {/* Reopen, inside the form (2026-10-13). SUPER_ADMIN only, hardcoded
                                    to match helper/Approval.php; the server re-checks the token. */}
                                {canReopen && (
                                    <Button
                                        variant="outlined"
                                        onClick={this.handleSendForApproval.bind(this, "PENDING")}
                                        style={{ borderColor: '#0b1c30', color: '#0b1c30', textTransform: 'none', fontWeight: 600, borderRadius: 8, padding: '10px 32px' }}
                                    >
                                        Reopen to Edit
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Insights section ─────────────────────────────────── */}
                {this.state.isEdit && (
                    <Row style={{ marginBottom: 24 }}>
                        <Col md={6}>
                            <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)', border: '1px solid #f8fafc' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                    <div style={{ padding: 8, background: '#ffdcc5', borderRadius: 8, display: 'flex' }}>
                                        <TrendingUpIcon style={{ color: '#703700', fontSize: 22 }} />
                                    </div>
                                    <span style={{ fontWeight: 700, fontSize: 16, color: '#0b1c30' }}>Spending Analysis</span>
                                </div>
                                <p style={{ fontSize: 14, color: '#464554', marginBottom: 16 }}>
                                    Weekly expenses total <strong style={{ color: '#a32f2f' }}>£ {formatAmount(totalExpense)}</strong> for this payment batch.
                                </p>
                                <div style={{ width: '100%', height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', background: '#a32f2f', borderRadius: 4, width: '70%' }} />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                                    <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' }}>CURRENT BATCH</span>
                                    <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' }}>BUDGET CAP</span>
                                </div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)', border: '1px solid #f8fafc', position: 'relative', overflow: 'hidden' }}>
                                <div style={{ position: 'absolute', inset: 0, opacity: 0.03, pointerEvents: 'none', backgroundImage: 'radial-gradient(circle at 2px 2px, #a32f2f 1px, transparent 0)', backgroundSize: '24px 24px' }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                        <div style={{ padding: 8, background: '#c9e6ff', borderRadius: 8, display: 'flex' }}>
                                            <AccountBalanceWalletIcon style={{ color: '#001e2f', fontSize: 22 }} />
                                        </div>
                                        <span style={{ fontWeight: 700, fontSize: 16, color: '#0b1c30' }}>Account Health</span>
                                    </div>
                                    <div style={{ fontSize: 28, fontWeight: 700, color: '#0b1c30', lineHeight: 1.2 }}>
                                        {this.state.ShopPayment?.label || '—'}
                                    </div>
                                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginTop: 4 }}>
                                        SELECTED ACCOUNT
                                    </div>
                                    <button style={{ marginTop: 16, background: 'none', border: 'none', color: '#a32f2f', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: 0 }}>
                                        View Account Details →
                                    </button>
                                </div>
                            </div>
                        </Col>
                    </Row>
                )}

                {/* ── Submit / Cancel (when not yet in edit mode) ──────── */}
                {!this.state.isEdit && (
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Button
                            variant="outlined"
                            onClick={this.handleExit.bind(this)}
                            style={{ borderColor: '#cbd5e1', color: '#475569', textTransform: 'none', fontWeight: 600 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={this.handleSubmitAccPayment.bind(this)}
                            style={{ background: '#a32f2f', textTransform: 'none', fontWeight: 600 }}
                        >
                            Add Payment
                        </Button>
                    </div>
                )}
            </Container>
        );
    }
}

export default withRouter(AccountPayments);
